import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import axiosInstance from '../../api';
import { dsVar } from '../../theme/tokens';

interface AgentOption {
    code: string;
    label: string;
}

/**
 * Narrow an ontology list to one activity template.
 *
 * Unlike the other filters this is code-valued over hundreds of options, so it is a
 * searchable Autocomplete rather than a select. Options come from the same query the
 * Agents catalog uses (`activity_class=AGENT&latest_only=true`), so the names offered
 * here are the ones the user saw there — and `latest_only` collapses clone families, so
 * the list is agents rather than every timestamped version.
 *
 * A deep link may name a template that is not in the fetched page (or not an AGENT at
 * all). That code is still shown as the selected value rather than silently cleared —
 * the filter is applied server-side regardless of what this picker happens to know.
 */
const ActivityFilter: React.FC<{
    value: string;
    onChange: (code: string) => void;
}> = ({ value, onChange }) => {
    const [options, setOptions] = useState<AgentOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get('/activity-templates', {
                    params: {
                        activity_class: 'AGENT',
                        latest_only: true,
                        limit: 200,
                    },
                    signal: controller.signal,
                });
                if (!active) return;
                const templates = res.data?.activity_templates ?? [];
                setOptions(templates
                    .filter((t: any) => t?.activity_template_code)
                    .map((t: any) => ({
                        code: t.activity_template_code,
                        label: t.activity_template_name || t.activity_template_code,
                    })));
            } catch (err: any) {
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                // A picker that cannot load its options must not break the page: the
                // filter still works when driven from the URL.
                console.error('Could not load activity templates for the filter:', err);
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, []);

    // Keep a deep-linked code selectable even when it is not among the fetched options.
    const selected = useMemo<AgentOption | null>(() => {
        if (!value) return null;
        return options.find((o) => o.code === value) ?? { code: value, label: value };
    }, [value, options]);

    return (
        <Autocomplete
            size="small"
            options={options}
            loading={loading}
            value={selected}
            onChange={(_event, option) => onChange(option?.code ?? '')}
            isOptionEqualToValue={(option, val) => option.code === val.code}
            getOptionLabel={(option) => option.label}
            sx={{ minWidth: 260 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Activity"
                    placeholder="Any agent"
                    sx={{
                        '& .MuiInputLabel-root': { color: dsVar('fg-2'), fontSize: '0.8125rem' },
                        '& .MuiInputLabel-root.Mui-focused': { color: dsVar('accent-fg') },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: dsVar('surface'),
                            color: dsVar('fg-body'),
                            fontSize: '0.875rem',
                            '& fieldset': { borderColor: dsVar('line') },
                            '&:hover fieldset': { borderColor: dsVar('line') },
                            '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                        },
                        '& .MuiSvgIcon-root': { color: dsVar('fg-muted') },
                    }}
                />
            )}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: dsVar('surface'),
                        color: dsVar('fg-body'),
                        border: `1px solid ${dsVar('line')}`,
                        backgroundImage: 'none',
                        '& .MuiAutocomplete-option': { fontSize: '0.875rem' },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: 'rgba(255, 222, 89, 0.12)',
                        },
                    },
                },
            }}
        />
    );
};

export default ActivityFilter;
